import { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Menu,
  MenuItem,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { shortenText } from '../helpers';

const ProjectRowActions = ({ projectId, projectName, projectStatus, onView, onDeleted, onStatusChanged }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);

  const [deleting, setDeleting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const open = Boolean(anchorEl);
  const statusOpen = Boolean(statusAnchorEl);

  const busy = deleting || statusSaving;

  const handleCloseMenu = () => setAnchorEl(null);

  const handleOpenStatusMenu = (e) => {
    // keep main menu open, open submenu anchored to this item
    setStatusAnchorEl(e.currentTarget);
  };

  const handleCloseStatusMenu = () => {
    setStatusAnchorEl(null);
  };

  const stop = (e) => {
    // Prevent bubbling to clickable row
    e.stopPropagation();
  };

  // Only allow transitions per your rules:
  // - archived -> in_progress only
  // - finished is just label (can go in_progress, can archive)
  const statusOptions = useMemo(() => {
    if (projectStatus === 'archived') {
      return [{ value: 'in_progress', label: 'In Progress' }];
    }
    if (projectStatus === 'finished') {
      return [
        { value: 'in_progress', label: 'In Progress' },
        { value: 'archived', label: 'Archived' },
      ];
    }
    // default in_progress
    return [
      { value: 'finished', label: 'Finished' },
      { value: 'archived', label: 'Archived' },
    ];
  }, [projectStatus]);

  // ---------- DELETE ----------
  const openConfirmDelete = () => {
    handleCloseStatusMenu();
    handleCloseMenu();
    setConfirmDeleteOpen(true);
  };

  const closeConfirmDelete = () => {
    if (!deleting) setConfirmDeleteOpen(false);
  };

  const confirmDelete = async () => {
    if (!projectId || deleting) return;

    try {
      setDeleting(true);
      await axios.delete(`/api/v1/project/${projectId}`);
      setConfirmDeleteOpen(false);
      onDeleted?.(projectId);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete project.';
      alert(msg);
    } finally {
      setDeleting(false);
    }
  };

  // ---------- STATUS CHANGE ----------
  const setStatus = async (nextStatus) => {
    if (!projectId || statusSaving) return;
    if (!nextStatus || nextStatus === projectStatus) return;

    try {
      setStatusSaving(true);

      const res = await axios.patch(`/api/v1/project/${projectId}`, {
        status: nextStatus,
      });

      const updated = res?.data?.data?.project || res?.data?.data?.doc || res?.data?.data || null;

      handleCloseStatusMenu();
      handleCloseMenu();

      if (updated?.id) {
        onStatusChanged?.(updated);
      } else {
        onStatusChanged?.({
          id: projectId,
          status: nextStatus,
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update status.';
      alert(msg);
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          stop(e);
          setAnchorEl(e.currentTarget);
        }}
        aria-controls={open ? 'project-actions-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={busy}
      >
        Actions
      </Button>

      {/* MAIN MENU */}
      <Menu
        id="project-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={busy ? undefined : handleCloseMenu}
        onClick={stop}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          disabled={!onView || busy}
          onClick={(e) => {
            stop(e);
            handleCloseStatusMenu();
            handleCloseMenu();
            onView?.(projectId);
          }}
        >
          View
        </MenuItem>

        {/* Status submenu trigger */}
        <MenuItem
          disabled={busy || !projectId}
          onClick={(e) => {
            stop(e);
            handleOpenStatusMenu(e);
          }}
          aria-haspopup="true"
          aria-expanded={statusOpen ? 'true' : undefined}
        >
          Status ▸
        </MenuItem>

        {/* SUBMENU */}
        <Menu
          anchorEl={statusAnchorEl}
          open={statusOpen}
          onClose={handleCloseStatusMenu}
          onClick={stop}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          MenuListProps={{ dense: true }}
        >
          {statusOptions.map((opt) => (
            <MenuItem
              key={opt.value}
              disabled={busy || opt.value === projectStatus}
              onClick={(e) => {
                stop(e);
                setStatus(opt.value);
              }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Menu>

        <Divider />

        <MenuItem
          disabled={busy || !projectId}
          onClick={(e) => {
            stop(e);
            openConfirmDelete();
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={confirmDeleteOpen} onClose={closeConfirmDelete} maxWidth="xs" fullWidth onClick={stop}>
        <DialogTitle>Delete project</DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText>
            Are you sure you want to delete <strong title={projectName || ''}>{shortenText(projectName, 40)}</strong>?
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={(e) => {
              stop(e);
              closeConfirmDelete();
            }}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={(e) => {
              stop(e);
              confirmDelete();
            }}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectRowActions;
